import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, fileUrl, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import Logo from "@/components/Logo";
import { LogOut, Calendar, MapPin, Clock, CheckCircle2, Play, Upload, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { getChecklistFor } from "@/lib/checklists";

export default function ProviderPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [openJob, setOpenJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [completionNotes, setCompletionNotes] = useState("");
  const [savingTask, setSavingTask] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/provider/jobs");
      setJobs(r.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/jobs/${id}`, { status });
      toast.success(t("portal.toast_marked", { status }));
      load();
      if (openJob?.id === id) setOpenJob({ ...openJob, status });
    } catch (e) { toast.error(formatError(e)); }
  };

  const uploadPhoto = async (id, type, files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder", `jobs/${id}/${type}`);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(r.data.id);
      }
      const job = jobs.find((j) => j.id === id);
      const field = type === "before" ? "photos_before" : "photos_after";
      const newPhotos = [...(job?.[field] || []), ...ids];
      await api.patch(`/jobs/${id}`, { [field]: newPhotos });
      toast.success(t("portal.toast_uploaded", { n: files.length }));
      const updated = { ...(openJob || {}), [field]: newPhotos };
      setOpenJob(updated);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); }
  };

  const toggleTask = async (jobId, taskKey) => {
    if (savingTask) return;
    const job = openJob;
    if (!job) return;
    const current = Array.isArray(job.checklist_progress) ? job.checklist_progress : [];
    const next = current.includes(taskKey)
      ? current.filter((k) => k !== taskKey)
      : [...current, taskKey];
    setOpenJob({ ...job, checklist_progress: next });
    setSavingTask(true);
    try {
      await api.patch(`/jobs/${jobId}`, { checklist_progress: next });
      // update local list state so home cards reflect progress on close
      setJobs((all) => all.map((j) => (j.id === jobId ? { ...j, checklist_progress: next } : j)));
    } catch (e) {
      toast.error(formatError(e));
      // Roll back
      setOpenJob({ ...job, checklist_progress: current });
    } finally { setSavingTask(false); }
  };

  const saveCompletion = async () => {
    try {
      await api.patch(`/jobs/${openJob.id}`, { completion_notes: completionNotes, status: "Completed" });
      toast.success(t("portal.toast_completed"));
      setOpenJob(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const handleLogout = () => { logout(); nav("/login"); };

  // Group jobs by day so cleaners see Today first, then Upcoming
  const grouped = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const todayJobs = [];
    const upcoming = [];
    const past = [];
    for (const j of jobs) {
      const d = (j.date || "").slice(0, 10);
      if (d === today) todayJobs.push(j);
      else if (d > today) upcoming.push(j);
      else past.push(j);
    }
    return { todayJobs, upcoming, past };
  }, [jobs]);

  return (
    <div className="min-h-screen bg-brand-appBg">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-slate-700">{user?.name}</div>
              <div className="text-xs text-slate-500">{t("portal.header_role")}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="provider-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 pb-24">
        <h1 className="font-heading text-2xl font-bold text-[#1B3A6B]">{t("portal.page_title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("portal.page_subtitle")}</p>

        {jobs.length === 0 && (
          <div className="mt-6 bg-white rounded-xl p-8 text-center text-slate-500 ring-1 ring-slate-200">
            {t("portal.empty")}
          </div>
        )}

        {grouped.todayJobs.length > 0 && (
          <JobSection t={t} title={t("portal.section_today")} jobs={grouped.todayJobs}
            openJob={(j) => { setOpenJob(j); setCompletionNotes(j.completion_notes || ""); }}
            onStart={(id) => updateStatus(id, "In Progress")} />
        )}
        {grouped.upcoming.length > 0 && (
          <JobSection t={t} title={t("portal.section_upcoming")} jobs={grouped.upcoming}
            openJob={(j) => { setOpenJob(j); setCompletionNotes(j.completion_notes || ""); }}
            onStart={(id) => updateStatus(id, "In Progress")} />
        )}
        {grouped.past.length > 0 && (
          <JobSection t={t} title={t("portal.section_past")} jobs={grouped.past} muted
            openJob={(j) => { setOpenJob(j); setCompletionNotes(j.completion_notes || ""); }}
            onStart={() => {}} />
        )}
      </main>

      <Dialog open={!!openJob} onOpenChange={(o) => !o && setOpenJob(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="provider-job-dialog">
          {openJob && (
            <>
              <DialogHeader><DialogTitle>{openJob.service_type}</DialogTitle></DialogHeader>
              <div className="text-sm space-y-2">
                <div><b className="text-[#1B3A6B]">{t("portal.detail_date")}:</b> {openJob.date} {openJob.start_time}</div>
                <div><b className="text-[#1B3A6B]">{t("portal.detail_address")}:</b> {openJob.address}, {openJob.city}</div>
                <div><b className="text-[#1B3A6B]">{t("portal.detail_hours")}:</b> {openJob.estimated_hours}</div>
                {openJob.supplies_needed && <div><b className="text-[#1B3A6B]">{t("portal.detail_supplies")}:</b> {openJob.supplies_needed}</div>}
                {openJob.equipment_needed && <div><b className="text-[#1B3A6B]">{t("portal.detail_equipment")}:</b> {openJob.equipment_needed}</div>}
                {openJob.access_instructions && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-2">
                    <b className="text-amber-900">{t("portal.detail_access")}:</b> <span className="text-amber-900">{openJob.access_instructions}</span>
                  </div>
                )}
                {openJob.customer_notes && (
                  <div className="bg-slate-50 rounded-md p-2"><b className="text-[#1B3A6B]">{t("portal.detail_customer_notes")}:</b> {openJob.customer_notes}</div>
                )}
              </div>

              <ChecklistBlock t={t} job={openJob} onToggle={(k) => toggleTask(openJob.id, k)} disabled={savingTask} />

              <div className="space-y-3">
                <PhotoBlock label={t("portal.photos_before")} addLabel={t("portal.add_photo")} photos={openJob.photos_before || []} onUpload={(files) => uploadPhoto(openJob.id, "before", files)} uploading={uploading} testid="provider-before" />
                <PhotoBlock label={t("portal.photos_after")} addLabel={t("portal.add_photo")} photos={openJob.photos_after || []} onUpload={(files) => uploadPhoto(openJob.id, "after", files)} uploading={uploading} testid="provider-after" />
              </div>

              <div>
                <label className="text-sm font-semibold text-[#1B3A6B]">{t("portal.completion_notes")}</label>
                <Textarea value={completionNotes} onChange={(e) => setCompletionNotes(e.target.value)} className="mt-1.5" data-testid="provider-completion-notes" />
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="ghost" onClick={() => setOpenJob(null)}>{t("portal.close")}</Button>
                <Button onClick={saveCompletion} className="bg-[#5BA83F] hover:bg-[#4A8A33] text-white h-11" data-testid="provider-save-completion">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> {t("portal.mark_completed")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function JobSection({ t, title, jobs, openJob, onStart, muted }) {
  return (
    <section className="mt-6">
      <h2 className={`text-xs uppercase font-bold tracking-wide mb-2 ${muted ? "text-slate-400" : "text-[#1B3A6B]"}`}>{title}</h2>
      <div className="space-y-3">
        {jobs.map((j) => {
          const total = getChecklistFor(j.service_type).length;
          const done = Array.isArray(j.checklist_progress) ? j.checklist_progress.length : 0;
          return (
            <div key={j.id} className={`bg-white rounded-xl ring-1 ring-slate-200 p-4 active:scale-[0.99] transition ${muted ? "opacity-70" : ""}`}
                 data-testid={`provider-job-${j.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-[#0F172A]">{j.service_type}</div>
                  <div className="text-sm text-slate-600 mt-0.5 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" /> {j.date} · <Clock className="w-3.5 h-3.5" /> {j.start_time || "TBD"}
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {j.address}, {j.city}</div>
                  {total > 0 && (
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                      <ListChecks className="w-3.5 h-3.5" />
                      <span data-testid={`provider-progress-${j.id}`}>{t("portal.tasks_count", { done, total })}</span>
                    </div>
                  )}
                </div>
                <StatusBadge status={j.status} className="shrink-0" />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-full h-10" onClick={() => openJob(j)} data-testid={`provider-open-${j.id}`}>
                  {t("portal.view_details")}
                </Button>
                {j.status !== "In Progress" && j.status !== "Completed" && !muted && (
                  <Button size="sm" className="rounded-full h-10 bg-[#5BA83F] hover:bg-[#4A8A33] text-white" onClick={() => onStart(j.id)} data-testid={`provider-start-${j.id}`}>
                    <Play className="w-4 h-4 mr-1" /> {t("portal.start")}
                  </Button>
                )}
                {j.status === "In Progress" && (
                  <Button size="sm" className="rounded-full h-10 bg-[#1B3A6B] hover:bg-[#152D54] text-white" onClick={() => openJob(j)} data-testid={`provider-complete-${j.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> {t("portal.complete")}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ChecklistBlock({ t, job, onToggle, disabled }) {
  const tasks = getChecklistFor(job.service_type);
  const doneSet = new Set(Array.isArray(job.checklist_progress) ? job.checklist_progress : []);
  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-[#1B3A6B] flex items-center gap-1.5">
          <ListChecks className="w-4 h-4" /> {t("portal.checklist_title")}
        </div>
        <span className="text-xs text-slate-500" data-testid="provider-checklist-count">{doneSet.size} / {tasks.length}</span>
      </div>
      <ul className="mt-2 space-y-1.5" data-testid="provider-checklist">
        {tasks.map((task) => {
          const done = doneSet.has(task.key);
          return (
            <li key={task.key}>
              <label className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md border transition cursor-pointer active:bg-slate-100 ${done ? "bg-[#E8F5E0] border-[#5BA83F]" : "bg-white border-slate-200 hover:border-slate-300"}`}
                     data-testid={`provider-task-${task.key}`}>
                <input type="checkbox" className="w-4 h-4 accent-[#5BA83F]" checked={done}
                       disabled={disabled} onChange={() => onToggle(task.key)} />
                <span className={`text-sm ${done ? "line-through text-slate-500" : "text-[#0F172A]"}`}>{task.label}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PhotoBlock({ label, addLabel, photos, onUpload, uploading, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-[#1B3A6B]">{label}</div>
        <label className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-dashed border-slate-300 cursor-pointer hover:bg-slate-50">
          <Upload className="w-3 h-3" /> {uploading ? "…" : addLabel}
          <input type="file" multiple accept="image/*" className="hidden" data-testid={`${testid}-upload`}
                 onChange={(e) => { onUpload(Array.from(e.target.files || [])); e.target.value = ""; }} />
        </label>
      </div>
      {photos.length > 0 && (
        <div className="mt-2 grid grid-cols-3 gap-2">
          {photos.map((p) => (
            <img key={p} src={fileUrl(p)} alt={label} className="rounded-md object-cover aspect-square w-full" />
          ))}
        </div>
      )}
    </div>
  );
}
